import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import Dialog from '@material-ui/core/Dialog';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { ProjectPageRedesignProvider } from 'components/ProjectPageRedesign/ProjectPageRedesignContext';
import { getRedesignDialogColors } from './redesignDialogColors';
import {
  getRedesignDialogFieldRules,
  REDESIGN_DIALOG_RADIUS,
  REDESIGN_DIALOG_SIZES,
} from './redesignDialogStyles';
import RedesignDialogFooter from './RedesignDialogFooter';
import RedesignDialogHeader from './RedesignDialogHeader';

// The paper's margin from the viewport (MUI's 32px, 16px on a phone).
const MARGIN = 32;
const MARGIN_XS = 16;

// Every paper rule under '&&': MUI's Dialog and Paper rules are one class
// each, and the field rules have to outrank sheets created after this one.
const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    const xs = theme.breakpoints.down('xs');
    const sizes = Object.keys(REDESIGN_DIALOG_SIZES).reduce(
      (rules, size) => ({
        ...rules,
        [`size${size.charAt(0).toUpperCase()}${size.slice(1)}`]: {
          '&&': { maxWidth: REDESIGN_DIALOG_SIZES[size] },
        },
      }),
      {}
    );
    return {
      // A neutral dim, not the theme's black at 50%.
      backdrop: {
        '&&': {
          backgroundColor: colors.backdrop,
        },
      },
      // The paper: the surface, its corner, a hairline drawn outside it (a
      // pane's fill can never cover it) and the soft large shadow. It does
      // not scroll itself; the body does.
      paper: {
        '&&': {
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          margin: MARGIN,
          width: `calc(100% - ${MARGIN * 2}px)`,
          maxHeight: `calc(100% - ${MARGIN * 2}px)`,
          overflow: 'hidden',
          backgroundColor: colors.surface,
          backgroundImage: 'none',
          color: colors.text,
          borderRadius: REDESIGN_DIALOG_RADIUS,
          boxShadow: `0 0 0 1px ${colors.edge}, ${colors.shadow}`,
          [xs]: {
            margin: MARGIN_XS,
            width: `calc(100% - ${MARGIN_XS * 2}px)`,
            maxHeight: `calc(100% - ${MARGIN_XS * 2}px)`,
          },
        },
      },
      // The house fields' look inside the paper (see redesignDialogStyles).
      fields: {
        '&&': getRedesignDialogFieldRules(colors),
      },
      ...sizes,
      // The viewport's height minus the margins: a split dialog's panes
      // scroll inside it.
      fullHeight: {
        '&&': {
          height: `calc(100% - ${MARGIN * 2}px)`,
          [xs]: {
            height: `calc(100% - ${MARGIN_XS * 2}px)`,
          },
        },
      },
      // After the rules above, so that it wins over them and their xs ones.
      fullScreen: {
        '&&': {
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: 'none',
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
      body: {
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        padding: theme.spacing(2.5, 3, 3),
        [xs]: {
          padding: theme.spacing(2),
        },
      },
      // bodyPadding="none": the content (a split) lays itself out.
      bodyFlush: {
        '&&': {
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        },
      },
      // Full screen without a footer: the body clears the home indicator.
      bodySafeArea: {
        '&&': {
          paddingBottom: `calc(${theme.spacing(
            2
          )}px + env(safe-area-inset-bottom, 0px))`,
        },
      },
    };
  },
  { name: 'LixRedesignDialog' }
);

const SIZE_CLASS = {
  sm: 'sizeSm',
  md: 'sizeMd',
  lg: 'sizeLg',
  xl: 'sizeXl',
};

// An empty slot draws no footer: nothing, false, '' or a list of those.
const isEmptyNode = (node) =>
  node === null ||
  node === undefined ||
  node === false ||
  node === '' ||
  (Array.isArray(node) && node.every(isEmptyNode));

let titleCount = 0;
const nextTitleId = () => {
  titleCount += 1;
  return `lix-redesign-dialog-title-${titleCount}`;
};

/**
 * The dialog of the redesigned views (the expense tracking, projects page
 * and project bank redesigns): a cool slate paper (white in the light theme)
 * with a flat header (`leading`, `title`, `subtitle`, `headerActions`, the
 * close button), a scrolling body and a footer (`footerStart` at the left,
 * `actions` at the right; none when both are empty). Full screen on phones
 * (`fullScreen="auto"`, below sm) with the header and footer in view.
 *
 * - `onClose(event, reason)`: the close button (reason `'closeButton'`),
 *   Escape (`'escapeKeyDown'`) and a click on the backdrop
 *   (`'backdropClick'`) call it, the last two unless `disableEscapeKeyDown`
 *   / `disableBackdropClick` (as components/DialogWrapper).
 * - `size`: the paper's maximum width — sm 480, md 720, lg 1040, xl 1360 —
 *   it is fullWidth up to that; `fullHeight` makes it as tall as the
 *   viewport minus its margins (for RedesignDialogSplit).
 * - `bodyPadding="none"` for a split or a body that lays itself out.
 * - `PaperProps`, `BackdropProps` (classNames merged), `TransitionProps`,
 *   `aria-describedby`, `className`, `data-*` and other MUI Dialog props pass
 *   through; the title is the dialog's `aria-labelledby`.
 *
 * The header, body and footer are in the redesign
 * (ProjectPageRedesignProvider value): house components that switch on
 * useProjectPageRedesign() render their redesigned look. A classic dialog
 * opened from inside — a confirmation, PayInvoiceDialog — must be wrapped in
 * `<ProjectPageRedesignProvider value={false}>`, since the context reaches
 * through its portal.
 *
 * Inside the paper the house fields take the dialog's look (TextInput,
 * TextInput select, Autocomplete inputs, DateTimePicker, checkboxes, radios,
 * switches; see getRedesignDialogFieldRules). Portaled menus and popovers —
 * the Select menu, the Autocomplete popper, tooltips — render outside the
 * paper and keep the theme's look for now.
 * @example
 * <RedesignDialog
 *   open
 *   onClose={() => setOpen(false)}
 *   title={t('expense.invoice')}
 *   subtitle={invoice.company_name}
 *   leading={<RedesignDialogStatus tone="success" icon={mdiCheck} label={t('expense.approved')} />}
 *   size="xl"
 *   fullHeight
 *   bodyPadding="none"
 *   footerStart={saving ? t('common.loading') : null}
 *   actions={
 *     <>
 *       <RedesignDialogButton kind="danger" onClick={reject}>{t('expense.reject_invoice')}</RedesignDialogButton>
 *       <RedesignDialogButton kind="primary" onClick={approve}>{t('expense.approve_invoice')}</RedesignDialogButton>
 *     </>
 *   }
 * >
 *   <RedesignDialogSplit main={<InvoiceFields />} aside={<InvoicePdf />} asideLabel={t('expense.invoice')} />
 * </RedesignDialog>
 */
const RedesignDialog = React.forwardRef(function RedesignDialog(props, ref) {
  const {
    open,
    onClose,
    title,
    subtitle,
    leading,
    headerActions,
    hideClose,
    actions,
    footerStart,
    size,
    fullHeight,
    fullScreen,
    bodyPadding,
    disableEscapeKeyDown,
    disableBackdropClick,
    PaperProps,
    BackdropProps,
    children,
    ...other
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
  const [titleId] = useState(nextTitleId);

  const isFullScreen = fullScreen === 'auto' ? phone : Boolean(fullScreen);
  const hasFooter = !isEmptyNode(actions) || !isEmptyNode(footerStart);
  const paperProps = PaperProps || {};
  const backdropProps = BackdropProps || {};

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
    if (onClose) onClose(event, reason);
  };

  return (
    <Dialog
      {...other}
      ref={ref}
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth={false}
      fullWidth
      fullScreen={isFullScreen}
      aria-labelledby={titleId}
      BackdropProps={{
        ...backdropProps,
        className: clsx(classes.backdrop, backdropProps.className),
      }}
      PaperProps={{
        'data-redesign-dialog': size,
        ...paperProps,
        className: clsx(
          classes.paper,
          classes.fields,
          classes[SIZE_CLASS[size]],
          fullHeight && classes.fullHeight,
          isFullScreen && classes.fullScreen,
          paperProps.className
        ),
      }}
    >
      <ProjectPageRedesignProvider value>
        <RedesignDialogHeader
          titleId={titleId}
          title={title}
          subtitle={subtitle}
          leading={leading}
          actions={headerActions}
          onClose={
            hideClose
              ? null
              : (event) => {
                  if (onClose) onClose(event, 'closeButton');
                }
          }
        />
        <div
          className={clsx(
            classes.body,
            bodyPadding === 'none' && classes.bodyFlush,
            isFullScreen && !hasFooter && classes.bodySafeArea
          )}
          data-redesign-dialog-body="true"
        >
          {children}
        </div>
        {hasFooter ? (
          <RedesignDialogFooter
            start={footerStart}
            actions={actions}
            safeArea={isFullScreen}
          />
        ) : null}
      </ProjectPageRedesignProvider>
    </Dialog>
  );
});

RedesignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** (event, reason): 'closeButton' | 'escapeKeyDown' | 'backdropClick'. */
  onClose: PropTypes.func,
  /** The heading, and the dialog's accessible name. */
  title: PropTypes.node.isRequired,
  /** A line under the title (muted). */
  subtitle: PropTypes.node,
  /** Before the title: a RedesignDialogStatus, an icon. */
  leading: PropTypes.node,
  /** Before the close button: RedesignDialogIconButtons. */
  headerActions: PropTypes.node,
  /** No close button (the dialog closes from its own actions). */
  hideClose: PropTypes.bool,
  /** The footer's right side: RedesignDialogButtons. */
  actions: PropTypes.node,
  /** The footer's left side: status text, progress. */
  footerStart: PropTypes.node,
  /** Maximum width: sm 480, md 720, lg 1040, xl 1360 (px). */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  /** As tall as the viewport minus the margins (for a split). */
  fullHeight: PropTypes.bool,
  /** 'auto' = full screen below sm. */
  fullScreen: PropTypes.oneOf(['auto', true, false]),
  /** 'none' for a RedesignDialogSplit or a body of its own. */
  bodyPadding: PropTypes.oneOf(['normal', 'none']),
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  PaperProps: PropTypes.object,
  BackdropProps: PropTypes.object,
  TransitionProps: PropTypes.object,
  'aria-describedby': PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

RedesignDialog.defaultProps = {
  size: 'md',
  fullScreen: 'auto',
  bodyPadding: 'normal',
};

export default RedesignDialog;
